import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { Link } from 'react-router-dom';
import { MapPin, Users, DollarSign, Navigation } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Fix default marker icons broken by webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const MapView = ({ halls }) => {
  const [, setSelectedHall] = useState(null);

  const hallsWithCoordinates = halls.filter(
    (hall) => hall.latitude && hall.longitude
  );

  const getMapCenter = () => {
    if (hallsWithCoordinates.length === 0) {
      return [15.8497, 74.4977]; // Default: Belagavi, India
    }
    const avgLat =
      hallsWithCoordinates.reduce((sum, hall) => sum + parseFloat(hall.latitude), 0) /
      hallsWithCoordinates.length;
    const avgLng =
      hallsWithCoordinates.reduce((sum, hall) => sum + parseFloat(hall.longitude), 0) /
      hallsWithCoordinates.length;
    return [avgLat, avgLng];
  };

  const openInGoogleMaps = (hall) => {
    const url = `https://www.google.com/maps/search/?api=1&query=${hall.latitude},${hall.longitude}`;
    window.open(url, '_blank');
  };

  if (hallsWithCoordinates.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 text-center">
        <MapPin className="mx-auto mb-4 text-gray-400" size={48} />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          No Halls with Location Data
        </h3>
        <p className="text-gray-600">
          Location coordinates are not available for the filtered halls.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <MapContainer
        center={getMapCenter()}
        zoom={13}
        style={{ width: '100%', height: '600px' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {hallsWithCoordinates.map((hall) => (
          <Marker
            key={hall.id}
            position={[parseFloat(hall.latitude), parseFloat(hall.longitude)]}
            eventHandlers={{ click: () => setSelectedHall(hall) }}
          >
            <Popup>
              <div className="p-1 max-w-xs">
                {hall.featured_image && (
                  <img
                    src={hall.featured_image}
                    alt={hall.name}
                    style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '6px', marginBottom: '8px' }}
                  />
                )}
                <h3 style={{ fontWeight: 'bold', fontSize: '15px', marginBottom: '6px' }}>
                  {hall.name}
                </h3>
                <div style={{ fontSize: '13px', color: '#555', marginBottom: '4px' }}>
                  <MapPin size={12} style={{ display: 'inline', marginRight: '4px' }} />
                  {hall.city}
                </div>
                <div style={{ fontSize: '13px', color: '#555', marginBottom: '4px' }}>
                  <Users size={12} style={{ display: 'inline', marginRight: '4px' }} />
                  Capacity: {hall.capacity} guests
                </div>
                <div style={{ fontSize: '13px', color: '#555', marginBottom: '10px' }}>
                  <DollarSign size={12} style={{ display: 'inline', marginRight: '4px' }} />
                  ₹{hall.celebration_price}/day
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <Link
                    to={`/hall/${hall.slug}`}
                    style={{
                      flex: 1,
                      background: '#dc2626',
                      color: '#fff',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      textAlign: 'center',
                      textDecoration: 'none',
                    }}
                  >
                    View Details
                  </Link>
                  <button
                    onClick={() => openInGoogleMaps(hall)}
                    style={{
                      background: '#f3f4f6',
                      color: '#374151',
                      padding: '6px 10px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                    title="Open in Google Maps"
                  >
                    <Navigation size={14} />
                  </button>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      <div className="bg-gray-50 px-4 py-3 border-t">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
            <span>Showing {hallsWithCoordinates.length} hall(s) on map</span>
          </div>
          <span className="text-xs text-gray-500">Click on markers for more details</span>
        </div>
      </div>
    </div>
  );
};

export default MapView;
